import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { UploadSimple, MagnifyingGlass, Trash, Storefront, Buildings, ArrowsClockwise, ShoppingCart, Printer, EnvelopeSimple, FileXls, FilePdf, FolderSimple, Lock } from "@phosphor-icons/react";
import { BestandenTab } from "@/components/BestandenTab";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { openDownload } from "@/lib/downloadUrl";
import { useAuth, usePermissions } from "@/App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VerkoopRij = ({ item, onVerwijder, onBestellen, geselecteerd, onToggle, leesmodus = false }) => {
  const [dx, setDx] = useState(0);
  const start = useRef(null);

  const onStart = (e) => {
    if (e.target.closest("button, input, [role='checkbox']")) return;
    start.current = e.clientX;
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onMove = (e) => {
    if (start.current === null) return;
    setDx(e.clientX - start.current);
  };
  const onEnd = () => {
    if (start.current === null) return;
    if (dx < -110) {
      setDx(-500);
      onVerwijder(item);
    } else if (dx > 110) {
      setDx(500);
      onBestellen(item);
    } else {
      setDx(0);
    }
    start.current = null;
  };

  return (
    <div className="relative overflow-hidden border-t border-border" data-testid={`kassa-rij-${item.id}`}>
      <div className={`absolute inset-0 flex items-center text-white text-xs font-bold uppercase tracking-wider ${dx > 0 ? "bg-[#1C7C54] justify-start pl-5" : "bg-[#C62828] justify-end pr-5"}`}>
        {dx > 0 ? (
          <>
            <ShoppingCart size={16} weight="bold" className="mr-1.5" />
            {dx > 110 ? "Laat los om bij te bestellen" : "Bijbestellen"}
          </>
        ) : (
          <>
            <Trash size={16} weight="bold" className="mr-1.5" />
            {dx < -110 ? "Laat los om te verwijderen" : "Uit stock"}
          </>
        )}
      </div>
      <div
        className="relative bg-white flex items-center justify-between gap-3 px-3 py-2.5 touch-pan-y select-none"
        style={{ transform: `translateX(${dx}px)`, transition: start.current === null ? "transform 180ms ease" : "none" }}
        onPointerDown={onStart}
        onPointerMove={onMove}
        onPointerUp={onEnd}
        onPointerCancel={onEnd}
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <Checkbox
            checked={geselecteerd}
            onCheckedChange={() => onToggle(item.id)}
            className="shrink-0"
            data-testid={`kassa-check-${item.id}`}
          />
          {/* Mobiel: compacte kaart met chips */}
          <div className="md:hidden min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2">
              <p className="text-base font-black font-mono whitespace-nowrap" data-testid={`kassa-artno-${item.id}`}>
                {item.artno || "—"}
              </p>
              <span className="shrink-0 text-sm font-black tabular-nums text-[#002FA7] bg-[#EAF1FF] rounded-full px-2.5 py-0.5" data-testid={`kassa-aantal-${item.id}`}>
                {item.aantal || 1}x
              </span>
            </div>
            <div className="flex items-center gap-1.5 mt-1 flex-wrap">
              <span className="text-sm font-bold truncate max-w-[60%]" data-testid={`kassa-omschrijving-${item.id}`}>
                {item.omschrijving || "—"}
              </span>
              {item.maat && (
                <span className="text-xs font-bold uppercase tracking-wider bg-muted rounded-sm px-2 py-0.5" data-testid={`kassa-maat-${item.id}`}>
                  {item.maat}
                </span>
              )}
              {item.kleur && (
                <span className="text-sm font-bold text-foreground/80 bg-muted rounded-sm px-2 py-0.5" data-testid={`kassa-kleur-${item.id}`}>
                  {item.kleur}
                </span>
              )}
            </div>
            <p className="text-xs font-semibold text-foreground/60 truncate mt-1">
              <span className="font-bold text-foreground/80">{item.leverancier || "—"}</span>
              {" · "}{item.filiaal_naam}
            </p>
            {item.opmerking && (
              <p
                className="md:hidden text-xs font-bold text-[#8a5a00] bg-[#FFF8E1] border border-[#F9A825]/40 rounded-sm px-2 py-1 mt-1.5 break-words whitespace-pre-wrap"
                data-testid={`kassa-opmerking-${item.id}`}
              >
                {item.opmerking}
              </p>
            )}
          </div>

          {/* Desktop: kolommen zoals in Excel */}
          <div className="hidden md:grid md:grid-cols-[150px_minmax(160px,1fr)_64px_96px_64px_120px_110px_minmax(120px,1fr)] gap-x-3 min-w-0 flex-1 items-center">
            <p className="text-sm font-bold font-mono whitespace-nowrap">{item.artno || "—"}</p>
            <p className="text-sm font-semibold truncate">{item.omschrijving || "—"}</p>
            <p className="text-sm font-semibold truncate">{item.maat || "—"}</p>
            <p className="text-sm font-bold truncate">{item.kleur || "—"}</p>
            <p className="text-sm font-bold tabular-nums truncate">{item.aantal || 1}</p>
            <p className="text-xs font-semibold truncate" data-testid={`kassa-leverancier-${item.id}`}>
              {item.leverancier || "—"}
            </p>
            <p className="text-[11px] text-muted-foreground truncate">{item.filiaal_naam || "—"}</p>
            <p
              className="text-xs font-bold text-[#8a5a00] truncate"
              title={item.opmerking || ""}
              data-testid={`kassa-opmerking-desktop-${item.id}`}
            >
              {item.opmerking || "—"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {leesmodus ? (
            <span className="text-[10px] font-semibold text-muted-foreground px-1" data-testid={`kassa-leesmodus-${item.id}`}>
              alleen lezen
            </span>
          ) : (
            <>
              <button
                type="button"
                title="Uit stock halen + op bestellijst zetten"
                onClick={() => onBestellen(item)}
                className="h-9 w-9 md:h-8 md:w-8 flex items-center justify-center rounded-sm border border-[#1C7C54]/40 text-[#1C7C54] md:hover:bg-[#1C7C54] md:hover:text-white transition-colors"
                data-testid={`kassa-bestellen-${item.id}`}
              >
                <ShoppingCart size={16} weight="bold" />
              </button>
              <button
                type="button"
                title="Uit stock halen"
                onClick={() => onVerwijder(item)}
                className="h-9 w-9 md:h-8 md:w-8 flex items-center justify-center rounded-sm border border-[#C62828]/40 text-[#C62828] md:hover:bg-[#C62828] md:hover:text-white transition-colors"
                data-testid={`kassa-verwijder-${item.id}`}
              >
                <Trash size={16} weight="bold" />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default function Kassa() {
  const auth = useAuth();
  const perms = usePermissions();
  const canImport = perms[auth?.role]?.kassa_import === true;
  const canBestanden = perms[auth?.role]?.bestanden === true || auth?.role === "superadmin";
  const canBestandenBeheren = perms[auth?.role]?.bestanden_beheren === true || auth?.role === "superadmin";
  // Vast gekoppeld filiaal: alles buiten dit filiaal is onbereikbaar (admin/superadmin niet)
  const isBeheer = auth?.role === "admin" || auth?.role === "superadmin";
  const vastFiliaal = !isBeheer && auth?.filiaal_id ? auth.filiaal_id : null;

  const [filialen, setFilialen] = useState([]);
  const [filterFiliaal, setFilterFiliaal] = useState(() => vastFiliaal || localStorage.getItem("kassa_filiaal") || "");
  const [filterLeverancier, setFilterLeverancier] = useState("alles");
  const [zoek, setZoek] = useState("");
  const [data, setData] = useState({ items: [], aantal_regels: 0, totaal_stuks: 0 });
  const [laden, setLaden] = useState(true);
  const [selectie, setSelectie] = useState([]);
  const [bulkBezig, setBulkBezig] = useState(false);
  const [tab, setTab] = useState("verkocht");
  const [bestel, setBestel] = useState({ items: [], aantal_regels: 0, totaal_stuks: 0 });
  const [bestelSelectie, setBestelSelectie] = useState([]);
  const [mailOpen, setMailOpen] = useState(false);
  const [mailAdres, setMailAdres] = useState("");
  const [mailBericht, setMailBericht] = useState("");
  const [mailBezig, setMailBezig] = useState(false);

  const [importFiliaal, setImportFiliaal] = useState("");
  const [voorkeurGezet, setVoorkeurGezet] = useState(false);
  const [bestand, setBestand] = useState(null);
  const [importeren, setImporteren] = useState(false);
  const fileRef = useRef(null);

  // Vast gekoppeld filiaal afdwingen — geen andere keuze mogelijk
  useEffect(() => {
    if (!vastFiliaal) return;
    setFilterFiliaal(vastFiliaal);
    setImportFiliaal(vastFiliaal);
    localStorage.setItem("kassa_filiaal", vastFiliaal);
  }, [vastFiliaal]);

  // Standaardfiliaal van de ingelogde gebruiker voorselecteren (blijft wijzigbaar)
  useEffect(() => {
    if (voorkeurGezet || vastFiliaal || !auth?.filiaal_id || filialen.length === 0) return;
    if (filialen.some((f) => f.id === auth.filiaal_id)) {
      setFilterFiliaal((v) => v || auth.filiaal_id);
      setImportFiliaal((v) => v || auth.filiaal_id);
    }
    setVoorkeurGezet(true);
  }, [auth?.filiaal_id, filialen, voorkeurGezet]);

  // Gekozen filiaal onthouden; verwijderen gebeurt altijd binnen dit filiaal
  const kiesFiliaal = (id) => {
    if (vastFiliaal) {
      toast.error("Je bent vast gekoppeld aan jouw filiaal — een ander filiaal kan je niet openen");
      return;
    }
    setFilterFiliaal(id);
    setImportFiliaal(id === "alles" ? "" : id);
    setSelectie([]);
    setBestelSelectie([]);
    if (id) localStorage.setItem("kassa_filiaal", id);
    else localStorage.removeItem("kassa_filiaal");
  };
  const actiefFiliaal = filterFiliaal && filterFiliaal !== "alles" ? filterFiliaal : null;
  const actiefFiliaalNaam = filialen.find((f) => f.id === actiefFiliaal)?.naam || "";

  const laad = useCallback(async () => {
    if (!filterFiliaal) return;
    setLaden(true);
    try {
      const params = {};
      if (filterFiliaal !== "alles") params.filiaal_id = filterFiliaal;
      if (filterLeverancier !== "alles") params.leverancier = filterLeverancier;
      if (zoek.trim()) params.zoek = zoek.trim();
      const res = await axios.get(`${API}/kassa`, { params });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kon de lijst niet ophalen");
    } finally {
      setLaden(false);
    }
  }, [filterFiliaal, filterLeverancier, zoek]);

  const laadBestel = useCallback(async () => {
    if (!filterFiliaal) return;
    try {
      const params = {};
      if (filterFiliaal !== "alles") params.filiaal_id = filterFiliaal;
      if (filterLeverancier !== "alles") params.leverancier = filterLeverancier;
      if (zoek.trim()) params.zoek = zoek.trim();
      const res = await axios.get(`${API}/kassa/bestellijst`, { params });
      setBestel(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kon de bestellijst niet ophalen");
    }
  }, [filterFiliaal, filterLeverancier, zoek]);

  useEffect(() => {
    axios.get(`${API}/filialen`).then((r) => {
      setFilialen(r.data);
      // Onthouden filiaal hoort bij de vorige firma? Dan opnieuw laten kiezen.
      setFilterFiliaal((v) => {
        if (v && v !== "alles" && !r.data.some((f) => f.id === v)) {
          localStorage.removeItem("kassa_filiaal");
          return "";
        }
        if (v && v !== "alles") setImportFiliaal((x) => x || v);
        return v;
      });
    }).catch(() => {});
  }, []);

  useEffect(() => { laadBestel(); }, [laadBestel]);

  useEffect(() => {
    const t = setTimeout(laad, zoek ? 350 : 0);
    return () => clearTimeout(t);
  }, [laad, zoek]);

  const doeImport = async () => {
    if (!bestand) { toast.error("Kies eerst een Excel-bestand"); return; }
    if (!importFiliaal) { toast.error("Kies een filiaal voor dit bestand"); return; }
    const form = new FormData();
    form.append("file", bestand);
    form.append("filiaal_id", importFiliaal);
    setImporteren(true);
    try {
      const res = await axios.post(`${API}/kassa/import`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const d = res.data;
      toast.success(`${d.geimporteerd} regels toegevoegd aan ${d.filiaal}${d.overgeslagen ? ` · ${d.overgeslagen} lege regels overgeslagen` : ""}`);
      setBestand(null);
      if (fileRef.current) fileRef.current.value = "";
      await laad();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import mislukt");
    } finally {
      setImporteren(false);
    }
  };

  useEffect(() => {
    setSelectie([]);
  }, [filterFiliaal, filterLeverancier, zoek]);

  const perFiliaalTeller = Object.values(
    data.items.reduce((acc, i) => {
      const naam = i.filiaal_naam || "Geen filiaal";
      acc[naam] = acc[naam] || { naam, stuks: 0 };
      acc[naam].stuks += i.aantal || 1;
      return acc;
    }, {}),
  ).sort((a, b) => b.stuks - a.stuks);

  const toggleSelectie = (id) =>
    setSelectie((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const allesGeselecteerd = data.items.length > 0 && selectie.length === data.items.length;

  const toggleAlles = () =>
    setSelectie(allesGeselecteerd ? [] : data.items.map((i) => i.id));

  const verwijderSelectie = async () => {
    if (selectie.length === 0) return;
    if (!actiefFiliaal) { toast.error("Kies eerst één filiaal — zo wis je nooit regels van een ander filiaal"); return; }
    if (!window.confirm(`${selectie.length} geselecteerde regel(s) van ${actiefFiliaalNaam} definitief verwijderen?`)) return;
    setBulkBezig(true);
    try {
      const res = await axios.post(`${API}/kassa/verwijder-bulk`, { ids: selectie, filiaal_id: actiefFiliaal });
      toast.success(res.data.message);
      setSelectie([]);
      await laad();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Verwijderen mislukt");
    } finally {
      setBulkBezig(false);
    }
  };

  const bestellen = async (item) => {
    if (!actiefFiliaal) { toast.error("Kies eerst één filiaal — in de leesmodus kan je niets verplaatsen"); return; }
    setData((prev) => ({
      ...prev,
      items: prev.items.filter((i) => i.id !== item.id),
      aantal_regels: prev.aantal_regels - 1,
      totaal_stuks: prev.totaal_stuks - (item.aantal || 1),
    }));
    try {
      await axios.post(`${API}/kassa/bestellen`, { ids: [item.id], filiaal_id: actiefFiliaal });
      toast.success(`${item.artno || item.omschrijving} op de bestellijst gezet`);
      laadBestel();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bijbestellen mislukt");
      laad();
    }
  };

  const bestelSelectie2 = async () => {
    if (selectie.length === 0) return;
    if (!actiefFiliaal) { toast.error("Kies eerst één filiaal"); return; }
    if (!window.confirm(`${selectie.length} artikel(en) van ${actiefFiliaalNaam} uit stock halen en op de bestellijst zetten?`)) return;
    setBulkBezig(true);
    try {
      const res = await axios.post(`${API}/kassa/bestellen`, { ids: selectie, filiaal_id: actiefFiliaal });
      toast.success(res.data.message);
      setSelectie([]);
      await Promise.all([laad(), laadBestel()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bijbestellen mislukt");
    } finally {
      setBulkBezig(false);
    }
  };

  const toggleBestelSelectie = (id) =>
    setBestelSelectie((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const alleBestelGeselecteerd = bestel.items.length > 0 && bestelSelectie.length === bestel.items.length;

  const verwijderBesteld = async () => {
    if (bestelSelectie.length === 0) return;
    if (!actiefFiliaal) { toast.error("Kies eerst één filiaal"); return; }
    if (!window.confirm(`${bestelSelectie.length} regel(s) van ${actiefFiliaalNaam} van de bestellijst halen? Doe dit pas nadat je besteld hebt.`)) return;
    setBulkBezig(true);
    try {
      const res = await axios.post(`${API}/kassa/bestellijst/verwijder-bulk`, { ids: bestelSelectie, filiaal_id: actiefFiliaal });
      toast.success(res.data.message);
      setBestelSelectie([]);
      await laadBestel();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Verwijderen mislukt");
    } finally {
      setBulkBezig(false);
    }
  };

  const bestellijstParams = () => {
    const p = new URLSearchParams();
    if (filterFiliaal !== "alles") p.set("filiaal_id", filterFiliaal);
    if (filterLeverancier !== "alles") p.set("leverancier", filterLeverancier);
    if (zoek.trim()) p.set("zoek", zoek.trim());
    return p.toString();
  };

  const downloadExcel = () => {
    const qs = bestellijstParams();
    openDownload(`${API}/kassa/bestellijst/excel${qs ? `?${qs}` : ""}`);
  };

  const downloadPdf = () => {
    const qs = bestellijstParams();
    openDownload(`${API}/kassa/bestellijst/pdf${qs ? `?${qs}` : ""}`);
  };

  const mailBestellijst = async () => {
    if (!mailAdres.trim()) { toast.error("Vul een e-mailadres in"); return; }
    setMailBezig(true);
    try {
      const res = await axios.post(`${API}/kassa/bestellijst/mail`, {
        email: mailAdres.trim(),
        filiaal_id: filterFiliaal === "alles" ? null : filterFiliaal,
        leverancier: filterLeverancier === "alles" ? null : filterLeverancier,
        zoek: zoek.trim() || null,
        bericht: mailBericht.trim() || null,
      });
      toast.success(res.data.message);
      setMailOpen(false);
      setMailBericht("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Mailen mislukt");
    } finally {
      setMailBezig(false);
    }
  };

  const verwijder = async (item) => {
    if (!actiefFiliaal) { toast.error("Kies eerst één filiaal — in de leesmodus kan je niets wissen"); return; }
    setData((prev) => ({
      ...prev,
      items: prev.items.filter((i) => i.id !== item.id),
      aantal_regels: prev.aantal_regels - 1,
      totaal_stuks: prev.totaal_stuks - (item.aantal || 1),
    }));
    try {
      await axios.delete(`${API}/kassa/${item.id}?filiaal_id=${encodeURIComponent(actiefFiliaal)}`);
      toast.success(`${item.artno || item.omschrijving} uit stock gehaald`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Verwijderen mislukt");
      laad();
    }
  };

  if (!filterFiliaal) {
    return (
      <div data-testid="kassa-filiaal-keuze" className="space-y-6">
        <div>
          <h1 className="page-title">Kassa / Verkocht</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Kies eerst het filiaal waarin je werkt. Je ziet en wist dan enkel regels van dat
            filiaal, nooit die van een ander filiaal.
          </p>
        </div>
        <div className="bg-white border border-border rounded-sm p-4 max-w-2xl">
          <p className="section-label mb-3">Voor welk filiaal werk je?</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {filialen.map((f) => (
              <button
                key={f.id}
                onClick={() => kiesFiliaal(f.id)}
                className="h-14 px-4 rounded-sm border border-border bg-white text-left font-bold hover:border-[#002FA7] hover:text-[#002FA7] transition-colors"
                data-testid={`kassa-kies-filiaal-${f.id}`}
              >
                {f.naam}
              </button>
            ))}
            {filialen.length === 0 && (
              <p className="text-sm text-muted-foreground">Nog geen filialen aangemaakt.</p>
            )}
          </div>
          {(auth?.role === "admin" || auth?.role === "superadmin") && (
            <button
              onClick={() => kiesFiliaal("alles")}
              className="mt-3 text-xs font-semibold text-muted-foreground underline"
              data-testid="kassa-kies-alles"
            >
              Alle filialen bekijken (alleen lezen — verwijderen kan dan niet)
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="kassa-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h1 className="page-title">Kassa / Verkocht</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Importeer de kassalijst en veeg een regel weg zodra het artikel uit stock is gehaald.
          </p>
          <div className="flex items-center gap-2 mt-2">
            <span className="text-xs font-black uppercase tracking-wider bg-[#002FA7] text-white rounded-sm px-2 py-1" data-testid="kassa-actief-filiaal">
              {actiefFiliaal ? actiefFiliaalNaam : "Alle filialen (alleen lezen)"}
            </span>
            {vastFiliaal ? (
              <span className="text-xs font-semibold text-muted-foreground" data-testid="kassa-vast-filiaal">
                <Lock size={11} weight="bold" className="inline mr-1 -mt-0.5" />
                Vast gekoppeld aan jouw filiaal
              </span>
            ) : (
              <button
                onClick={() => kiesFiliaal("")}
                className="text-xs font-semibold text-[#002FA7] underline"
                data-testid="kassa-wissel-filiaal"
              >
                Ander filiaal
              </button>
            )}
          </div>
        </div>
        <Button variant="outline" onClick={laad} className="rounded-sm shrink-0" data-testid="kassa-refresh">
          <ArrowsClockwise size={14} weight="bold" className="mr-1.5" /> Verversen
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-1 p-1 bg-muted rounded-sm max-w-xl print:hidden" data-testid="kassa-tabs">
        <button
          onClick={() => setTab("verkocht")}
          className={`flex items-center justify-center gap-1.5 text-sm font-black uppercase tracking-wider py-3 rounded-sm transition-colors ${tab === "verkocht" ? "bg-white text-[#002FA7] shadow-sm" : "text-muted-foreground"}`}
          data-testid="tab-verkocht"
        >
          <Storefront size={14} weight="bold" /> Uit stock halen
        </button>
        <button
          onClick={() => setTab("bestellijst")}
          className={`flex items-center justify-center gap-1.5 text-sm font-black uppercase tracking-wider py-3 rounded-sm transition-colors ${tab === "bestellijst" ? "bg-white text-[#1C7C54] shadow-sm" : "text-muted-foreground"}`}
          data-testid="tab-bestellijst"
        >
          <ShoppingCart size={14} weight="bold" /> Bestellijst
          {bestel.aantal_regels > 0 && (
            <span className="ml-1 text-[10px] bg-[#1C7C54] text-white rounded-full px-1.5">{bestel.aantal_regels}</span>
          )}
        </button>
        {canBestanden && (
          <button
            onClick={() => setTab("bestanden")}
            className={`flex items-center justify-center gap-1.5 text-sm font-black uppercase tracking-wider py-3 rounded-sm transition-colors ${tab === "bestanden" ? "bg-white text-[#7C3AED] shadow-sm" : "text-muted-foreground"}`}
            data-testid="tab-bestanden"
          >
            <FolderSimple size={14} weight="bold" /> Bestanden
          </button>
        )}
      </div>

      {tab === "bestanden" ? (
        <BestandenTab canBeheren={canBestandenBeheren} />
      ) : tab === "bestellijst" ? (
        <div className="bg-white border border-border rounded-sm overflow-hidden" data-testid="bestellijst-blok">
          <div className="px-4 py-3 border-b border-border flex flex-wrap items-center justify-between gap-2">
            <div>
              <h2 className="font-bold text-sm" style={{ fontFamily: "Chivo, sans-serif" }}>Bij te bestellen</h2>
              <p className="text-xs text-muted-foreground">
                {bestel.aantal_regels} regel{bestel.aantal_regels === 1 ? "" : "s"} · {bestel.totaal_stuks} stuk{bestel.totaal_stuks === 1 ? "" : "s"}
                {filterFiliaal !== "alles" && ` · ${filialen.find((f) => f.id === filterFiliaal)?.naam || ""}`}
              </p>
            </div>
            <div className="flex items-center gap-2 print:hidden">
              <Button onClick={downloadPdf} className="rounded-sm h-9 text-xs bg-[#002FA7] hover:bg-[#002280] text-white" data-testid="bestellijst-pdf">
                <FilePdf size={14} weight="bold" className="mr-1.5" /> PDF bestelbon
              </Button>
              <Button variant="outline" onClick={() => window.print()} className="rounded-sm h-9 text-xs" data-testid="bestellijst-print">
                <Printer size={14} weight="bold" className="mr-1.5" /> Afdrukken
              </Button>
              <Button variant="outline" onClick={downloadExcel} className="rounded-sm h-9 text-xs" data-testid="bestellijst-excel">
                <FileXls size={14} weight="bold" className="mr-1.5" /> Excel
              </Button>
              <Button variant="outline" onClick={() => setMailOpen(true)} className="rounded-sm h-9 text-xs" data-testid="bestellijst-mail-btn">
                <EnvelopeSimple size={14} weight="bold" className="mr-1.5" /> Mailen
              </Button>
              <Button
                onClick={verwijderBesteld}
                disabled={bulkBezig || bestelSelectie.length === 0}
                className="rounded-sm h-9 text-xs bg-[#C62828] hover:bg-[#A31F1F] text-white"
                data-testid="bestellijst-verwijder"
              >
                <Trash size={14} weight="bold" className="mr-1.5" /> Besteld · verwijder {bestelSelectie.length || ""}
              </Button>
            </div>
          </div>

          <div className="p-4 grid grid-cols-1 sm:grid-cols-4 gap-3 border-b border-border print:hidden">
            <div>
              <Label className="text-sm font-bold">Filiaal</Label>
              <Select value={filterFiliaal} onValueChange={kiesFiliaal} disabled={!!vastFiliaal}>
                <SelectTrigger className="rounded-sm mt-1.5" data-testid="bestel-filter-filiaal"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {!vastFiliaal && <SelectItem value="alles">Alle filialen</SelectItem>}
                  {filialen.filter((f) => !vastFiliaal || f.id === vastFiliaal).map((f) => (<SelectItem key={f.id} value={f.id}>{f.naam}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-bold">Leverancier</Label>
              <Select value={filterLeverancier} onValueChange={setFilterLeverancier}>
                <SelectTrigger className="rounded-sm mt-1.5" data-testid="bestel-filter-leverancier"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="alles">Alle leveranciers</SelectItem>
                  {(bestel.leveranciers || []).map((l) => (<SelectItem key={l} value={l}>{l}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-sm font-bold">Zoek artikel</Label>
              <div className="relative mt-1.5">
                <MagnifyingGlass size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={zoek}
                  onChange={(e) => setZoek(e.target.value)}
                  placeholder="ArtNo, omschrijving, kleur of maat..."
                  className="rounded-sm pl-8"
                  data-testid="bestel-zoek"
                />
              </div>
            </div>
          </div>

          <div className="px-3 py-2 bg-[#EAF8F0] flex items-center gap-2.5 print:hidden">
            <Checkbox
              checked={alleBestelGeselecteerd}
              onCheckedChange={() => setBestelSelectie(alleBestelGeselecteerd ? [] : bestel.items.map((i) => i.id))}
              disabled={bestel.items.length === 0}
              data-testid="bestellijst-check-alles"
            />
            <span className="text-xs font-bold uppercase tracking-wider text-[#1C7C54]">
              {bestelSelectie.length > 0 ? `${bestelSelectie.length} geselecteerd` : "Selecteer alles"}
            </span>
          </div>

          {bestel.items.length === 0 ? (
            <div className="p-10 text-center" data-testid="bestellijst-leeg">
              <ShoppingCart size={28} className="mx-auto text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">Nog niets om bij te bestellen</p>
            </div>
          ) : (
            <div data-testid="bestellijst-tabel">
              {(bestel.groepen || []).map((groep) => (
                <div key={groep.leverancier} className="border-t border-border">
                  <div className="px-3 py-2 bg-[#EAF8F0] flex items-center justify-between gap-2">
                    <p className="text-xs font-black uppercase tracking-wider text-[#1C7C54]" data-testid={`bestel-groep-${groep.leverancier}`}>
                      {groep.leverancier}
                    </p>
                    <p className="text-xs font-bold tabular-nums text-[#1C7C54]">
                      {groep.aantal_artikelen} artikel{groep.aantal_artikelen === 1 ? "" : "en"} · {groep.totaal_stuks} stuks
                    </p>
                  </div>
                  <div className="overflow-x-auto">
                  <table className="w-full text-xs min-w-[640px]">
                    <thead className="bg-muted">
                      <tr className="text-[10px] uppercase tracking-wider text-muted-foreground">
                        <th className="px-3 py-1.5 text-left print:hidden w-8"></th>
                        <th className="px-3 py-1.5 text-left w-[150px]">ArtNo</th>
                        <th className="px-3 py-1.5 text-left">Omschrijving</th>
                        <th className="px-3 py-1.5 text-left w-[64px]">Maat</th>
                        <th className="px-3 py-1.5 text-left w-[96px]">Kleur</th>
                        <th className="px-3 py-1.5 text-right w-[70px]">Bestellen</th>
                        <th className="px-3 py-1.5 text-left w-[110px]">Filiaal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groep.regels.map((r) => {
                        const allenGeselecteerd = r.ids.every((id) => bestelSelectie.includes(id));
                        return (
                          <tr key={r.ids.join("-")} className="border-t border-border" data-testid={`bestel-rij-${r.ids[0]}`}>
                            <td className="px-3 py-1.5 print:hidden">
                              <Checkbox
                                checked={allenGeselecteerd}
                                onCheckedChange={() =>
                                  setBestelSelectie((prev) =>
                                    allenGeselecteerd
                                      ? prev.filter((id) => !r.ids.includes(id))
                                      : [...new Set([...prev, ...r.ids])],
                                  )
                                }
                                data-testid={`bestel-check-${r.ids[0]}`}
                              />
                            </td>
                            <td className="px-3 py-1.5 font-mono font-bold whitespace-nowrap">{r.artno || "—"}</td>
                            <td className="px-3 py-1.5">{r.omschrijving || "—"}</td>
                            <td className="px-3 py-1.5">{r.maat || "—"}</td>
                            <td className="px-3 py-1.5">{r.kleur || "—"}</td>
                            <td className="px-3 py-1.5 text-right">
                              <span className="font-black tabular-nums text-[#1C7C54] text-sm" data-testid={`bestel-aantal-${r.ids[0]}`}>
                                {r.aantal}x
                              </span>
                            </td>
                            <td className="px-3 py-1.5 text-muted-foreground">{r.filialen || "—"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  </div>
                </div>
              ))}
              <div className="px-3 py-3 bg-[#EAF8F0] border-t-2 border-[#1C7C54] flex items-center justify-between">
                <p className="text-xs font-black uppercase tracking-wider text-[#1C7C54]">Totaal te bestellen</p>
                <p className="text-base font-black tabular-nums text-[#1C7C54]" data-testid="bestellijst-totaal">
                  {bestel.totaal_stuks}
                </p>
              </div>
            </div>
          )}
        </div>
      ) : (
      <>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4" data-testid="kassa-teller">
        <div className="stat-card">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-9 h-9 rounded-sm bg-[#002FA7]/10 flex items-center justify-center">
              <Storefront size={20} weight="bold" className="text-[#002FA7]" />
            </div>
            <span className="section-label">Uit stock halen</span>
          </div>
          <p className="text-3xl font-black" style={{ fontFamily: "Chivo, sans-serif" }} data-testid="kassa-teller-stuks">
            {data.totaal_stuks}
          </p>
          <p className="text-xs font-semibold text-foreground/60 mt-1">stuks te nemen</p>
        </div>
        <div className="stat-card">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-9 h-9 rounded-sm bg-muted flex items-center justify-center">
              <Trash size={20} weight="bold" className="text-muted-foreground" />
            </div>
            <span className="section-label">Regels</span>
          </div>
          <p className="text-3xl font-black" style={{ fontFamily: "Chivo, sans-serif" }} data-testid="kassa-teller-regels">
            {data.aantal_regels}
          </p>
          <p className="text-[11px] text-muted-foreground mt-1">
            {filterFiliaal === "alles" ? "alle filialen" : filialen.find((f) => f.id === filterFiliaal)?.naam || ""}
          </p>
        </div>
        <div className="stat-card col-span-2 sm:col-span-1">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-9 h-9 rounded-sm bg-[#38A169]/10 flex items-center justify-center">
              <Buildings size={20} weight="bold" className="text-[#38A169]" />
            </div>
            <span className="section-label">Per filiaal</span>
          </div>
          {perFiliaalTeller.length === 0 ? (
            <p className="text-xs text-muted-foreground">Niets openstaand</p>
          ) : (
            <div className="space-y-0.5">
              {perFiliaalTeller.map((f) => (
                <div key={f.naam} className="flex items-center justify-between gap-2 text-sm font-medium">
                  <span className="truncate">{f.naam}</span>
                  <span className="font-bold tabular-nums text-[#002FA7]">{f.stuks}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {canImport && (
        <div className="bg-white border border-border rounded-sm p-5" data-testid="kassa-import-blok">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-sm bg-primary/10 flex items-center justify-center">
              <UploadSimple size={18} weight="bold" className="text-[#002FA7]" />
            </div>
            <div>
              <h3 className="font-bold text-sm" style={{ fontFamily: "Chivo, sans-serif" }}>Excel importeren</h3>
              <p className="text-sm font-medium text-foreground/70">Kolommen: ArtNo, Leverancier, AANTAL, Omschrijving, Maat, Kleur, Opmerking. Leeg AANTAL = 1 stuk.</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-sm font-bold">Filiaal voor dit bestand</Label>
              <Select value={importFiliaal} onValueChange={setImportFiliaal} disabled={!!vastFiliaal}>
                <SelectTrigger className="rounded-sm mt-1.5" data-testid="kassa-import-filiaal"><SelectValue placeholder="Kies filiaal..." /></SelectTrigger>
                <SelectContent>
                  {filialen.filter((f) => !vastFiliaal || f.id === vastFiliaal).map((f) => (<SelectItem key={f.id} value={f.id}>{f.naam}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-bold">Excel-bestand (.xlsx)</Label>
              <Input
                ref={fileRef}
                type="file"
                accept=".xlsx,.xlsm"
                onChange={(e) => setBestand(e.target.files?.[0] || null)}
                className="rounded-sm mt-1.5"
                data-testid="kassa-bestand"
              />
            </div>
            <div className="flex items-end">
              <Button
                onClick={doeImport}
                disabled={importeren}
                className="rounded-sm w-full bg-[#002FA7] hover:bg-[#002280] text-white h-10"
                data-testid="kassa-import-btn"
              >
                <UploadSimple size={15} weight="bold" className="mr-1.5" />
                {importeren ? "Importeren..." : "Importeren"}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white border border-border rounded-sm overflow-hidden" data-testid="kassa-lijst">
        <div className="p-4 grid grid-cols-1 sm:grid-cols-4 gap-3 border-b border-border">
          <div>
            <Label className="text-sm font-bold">Filiaal</Label>
            <Select value={filterFiliaal} onValueChange={kiesFiliaal} disabled={!!vastFiliaal}>
              <SelectTrigger className="rounded-sm mt-1.5" data-testid="kassa-filter-filiaal"><SelectValue /></SelectTrigger>
              <SelectContent>
                {!vastFiliaal && <SelectItem value="alles">Alle filialen</SelectItem>}
                {filialen.filter((f) => !vastFiliaal || f.id === vastFiliaal).map((f) => (<SelectItem key={f.id} value={f.id}>{f.naam}</SelectItem>))}
              </SelectContent>
            </Select>
            {vastFiliaal && (
              <p className="text-[11px] font-semibold text-[#002FA7] mt-1" data-testid="kassa-vast-filiaal-note">
                <Lock size={11} weight="bold" className="inline mr-1 -mt-0.5" />
                Jouw filiaal staat vast — je ziet nooit gegevens van een ander filiaal
              </p>
            )}
            {!vastFiliaal && auth?.filiaal_id && filterFiliaal === auth.filiaal_id && (
              <p className="text-[11px] font-semibold text-[#002FA7] mt-1" data-testid="kassa-standaard-filiaal">
                Jouw standaardfiliaal — je kan hierboven een ander filiaal kiezen
              </p>
            )}
          </div>
          <div>
            <Label className="text-sm font-bold">Leverancier</Label>
            <Select value={filterLeverancier} onValueChange={setFilterLeverancier}>
              <SelectTrigger className="rounded-sm mt-1.5" data-testid="kassa-filter-leverancier"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="alles">Alle leveranciers</SelectItem>
                {(data.leveranciers || []).map((l) => (<SelectItem key={l} value={l}>{l}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2">
            <Label className="text-sm font-bold">Zoek artikel</Label>
            <div className="relative mt-1.5">
              <MagnifyingGlass size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={zoek}
                onChange={(e) => setZoek(e.target.value)}
                placeholder="ArtNo, omschrijving, kleur of maat..."
                className="rounded-sm pl-8"
                data-testid="kassa-zoek"
              />
            </div>
          </div>
        </div>

        <div className="px-4 py-2.5 bg-[#EAF1FF] flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2.5">
            <Checkbox
              checked={allesGeselecteerd}
              onCheckedChange={toggleAlles}
              disabled={data.items.length === 0}
              data-testid="kassa-check-alles"
            />
            <p className="text-sm font-black uppercase tracking-wider text-[#002FA7]" data-testid="kassa-selectie-info">
              {selectie.length > 0
                ? `${selectie.length} geselecteerd`
                : `${data.aantal_regels} regel${data.aantal_regels === 1 ? "" : "s"}`}
              {filterFiliaal !== "alles" && (
                <span className="ml-1.5 font-semibold normal-case tracking-normal text-[#002FA7]/70">
                  {" · "}{filialen.find((f) => f.id === filterFiliaal)?.naam || ""}
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {selectie.length > 0 && (
              <Button
                onClick={bestelSelectie2}
                disabled={bulkBezig}
                className="rounded-sm h-8 text-xs bg-[#1C7C54] hover:bg-[#15603F] text-white"
                data-testid="kassa-bestellen-selectie"
              >
                <ShoppingCart size={13} weight="bold" className="mr-1" /> Bestellen {selectie.length}
              </Button>
            )}
            {selectie.length > 0 && (
              <Button
                onClick={verwijderSelectie}
                disabled={bulkBezig}
                className="rounded-sm h-8 text-xs bg-[#C62828] hover:bg-[#A31F1F] text-white"
                data-testid="kassa-verwijder-selectie"
              >
                <Trash size={13} weight="bold" className="mr-1" />
                {bulkBezig ? "Verwijderen..." : `Verwijder ${selectie.length}`}
              </Button>
            )}
            <p className="text-base font-black tabular-nums text-[#002FA7]" data-testid="kassa-totaal">
              {data.totaal_stuks} stuk{data.totaal_stuks === 1 ? "" : "s"}
            </p>
          </div>
        </div>

        {laden ? (
          <div className="p-6 text-sm text-muted-foreground">Laden...</div>
        ) : data.items.length === 0 ? (
          <div className="p-10 text-center" data-testid="kassa-leeg">
            <Storefront size={28} className="mx-auto text-muted-foreground mb-2" />
            <p className="text-base font-semibold text-foreground/70">Niets in de lijst — importeer een kassabestand</p>
          </div>
        ) : (
          <>
            <p className="px-4 py-2 text-xs font-semibold text-foreground/60 md:hidden">
              ← Veeg naar links = uit stock · veeg naar rechts = uit stock + bijbestellen →
            </p>
            <div className="md:overflow-x-auto">
            <div className="md:min-w-[1060px]">
            <div className="hidden md:flex items-center gap-3 px-3 py-1.5 bg-muted text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              <span className="w-4" />
              <div className="grid grid-cols-[150px_minmax(160px,1fr)_64px_96px_64px_120px_110px_minmax(120px,1fr)] gap-x-3 flex-1">
                <span>ArtNo</span>
                <span>Omschrijving</span>
                <span>Maat</span>
                <span>Kleur</span>
                <span>Aantal</span>
                <span>Leverancier</span>
                <span>Filiaal</span>
                <span>Opmerking</span>
              </div>
              <span className="w-[72px]" />
            </div>
            {data.items.map((item) => (
              <VerkoopRij
                key={item.id}
                item={item}
                onVerwijder={verwijder}
                onBestellen={bestellen}
                leesmodus={!actiefFiliaal}
                geselecteerd={selectie.includes(item.id)}
                onToggle={toggleSelectie}
              />
            ))}
            </div>
            </div>
          </>
        )}
      </div>
      </>
      )}

      <Dialog open={mailOpen} onOpenChange={setMailOpen}>
        <DialogContent className="sm:max-w-md" data-testid="bestellijst-mail-dialog">
          <DialogHeader>
            <DialogTitle>Bestellijst mailen</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-sm font-bold">E-mailadres leverancier</Label>
              <Input
                type="email"
                value={mailAdres}
                onChange={(e) => setMailAdres(e.target.value)}
                placeholder="inkoop@leverancier.be"
                className="rounded-sm mt-1.5"
                data-testid="mail-adres"
              />
            </div>
            <div>
              <Label className="text-sm font-bold">Bericht (optioneel)</Label>
              <Input
                value={mailBericht}
                onChange={(e) => setMailBericht(e.target.value)}
                placeholder="Graag deze artikelen bijbestellen"
                className="rounded-sm mt-1.5"
                data-testid="mail-bericht"
              />
            </div>
            <p className="text-[11px] text-muted-foreground">
              De lijst gaat mee in de mail én als PDF- en Excel-bijlage, volgens de actieve filters
              ({filterLeverancier === "alles" ? "alle leveranciers" : filterLeverancier},{" "}
              {filterFiliaal === "alles" ? "alle filialen" : filialen.find((f) => f.id === filterFiliaal)?.naam || ""}).
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMailOpen(false)} className="rounded-sm">Annuleren</Button>
            <Button onClick={mailBestellijst} disabled={mailBezig} className="rounded-sm bg-[#1C7C54] hover:bg-[#15603F] text-white" data-testid="mail-verstuur">
              <EnvelopeSimple size={14} weight="bold" className="mr-1.5" />
              {mailBezig ? "Versturen..." : "Versturen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
